import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const SubscribingChannels: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          For Viewers
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Subscribing to Channels
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Subscriptions let you follow your favorite creators and get notified when they upload new content. Learn how to subscribe, manage notifications, and organize your subscriptions.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How to Subscribe
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Visit a channel page or watch one of their videos</li>
          <li>Click the red "Subscribe" button below the video or on the channel page</li>
          <li>Button changes to "Subscribed" with a checkmark</li>
          <li>You're now following that channel</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Notification Levels
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          After subscribing, click the bell icon next to "Subscribed" to choose notification level:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>All:</strong> Notified for every new upload (recommended for favorite creators)</li>
          <li><strong>Personalized:</strong> Notified for some uploads based on watch history</li>
          <li><strong>None:</strong> No notifications, but channel appears in Subscriptions feed</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Viewing Subscriptions
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Click "Subscriptions" in the sidebar</li>
          <li>See latest uploads from all subscribed channels in chronological order</li>
          <li>Desktop: Channels appear as icons at top of page for quick access</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Managing Subscriptions
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          To see all your subscriptions:
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Go to Profile → Subscriptions</li>
          <li>View full list of channels you follow</li>
          <li>Sorted by most recent activity</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Unsubscribing
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Visit the channel page</li>
          <li>Click "Subscribed" button</li>
          <li>Select "Unsubscribe" from dropdown</li>
          <li>Or from your subscriptions list: click three-dot menu → Unsubscribe</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Tips for Managing Subscriptions
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Be selective:</strong> Subscribe only to channels you actively watch (quality {'>'} quantity)</li>
          <li><strong>Review regularly:</strong> Unsubscribe from inactive or irrelevant channels every few months</li>
          <li><strong>Use notification levels:</strong> Set "All" for 5-10 favorites, "None" for others</li>
          <li><strong>Organize by viewing habits:</strong> Channels you watch weekly deserve "All" notifications</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Subscription Limits
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          You can subscribe to up to 1,000 channels. If you hit this limit, unsubscribe from inactive channels before adding new ones.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Privacy Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          By default, your subscriptions are public. To make them private:
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Go to Settings → Privacy</li>
          <li>Toggle "Keep my subscriptions private"</li>
          <li>Only you can see which channels you follow</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Subscription Issues?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Not receiving notifications or subscription feed not updating? Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SubscribingChannels;
